import {
  DICT_VERSION,
  Dictionary,
  SCHEMA_VERSION,
  constantsManifest,
  validateBoard,
  verifyChecksum,
  type PuzzleDef,
  type RankThresholds,
} from './engine';

/**
 * Longshot does NOT come from Firestore. Its days are published by the admin
 * panel to the `puzzle-content` R2 bucket (`v1/longshot/{YYYY-MM-DD}.json`) and
 * served by the public read Worker — the same host iOS reads from via the
 * `content_base_url` Remote Config key. The Worker sends
 * `access-control-allow-origin: *` and caches days at the edge for 24h, so the
 * browser reads it directly; there is no quota to shield behind a proxy.
 */
const CONTENT_BASE_URL =
  import.meta.env.VITE_CONTENT_BASE_URL ??
  'https://puzzle-content-read.karpovych-artem.workers.dev';

export async function fetchLongshotDay(dayKey: string): Promise<PuzzleDef | null> {
  const r = await fetch(`${CONTENT_BASE_URL}/v1/longshot/${encodeURIComponent(dayKey)}.json`, {
    headers: { accept: 'application/json' },
  });
  if (r.status === 404) return null;
  if (!r.ok) throw new Error(`Longshot server returned ${r.status}`);
  const puzzle = (await r.json()) as PuzzleDef;
  validatePuzzle(puzzle);
  return puzzle;
}

/**
 * Mirrors `LongshotPuzzle.validate()` on iOS. A board this build cannot score
 * the same way everyone else does is refused outright, not played leniently.
 */
function validatePuzzle(p: PuzzleDef): void {
  if (!p || typeof p.board !== 'string') throw new Error('This puzzle could not be verified.');
  if (p.schema > SCHEMA_VERSION) {
    throw new Error('This puzzle was built for a newer version of the game. Reload to update.');
  }
  const expected = constantsManifest();
  const c = p.constants ?? ({} as PuzzleDef['constants']);
  const constantsMatch = (Object.keys(expected) as (keyof typeof expected)[]).every(
    (k) => c[k] === expected[k],
  );
  if (p.dictVersion !== DICT_VERSION || !constantsMatch) {
    throw new Error('This puzzle was built for different rules. Reload to update.');
  }
  validateBoard(p.board, p.gold);
  if (!verifyChecksum(p.board, p.gold, p.constants, p.checksum)) {
    throw new Error('This puzzle could not be verified.');
  }
}

/** The board's shipped rank ladder, or null if it is malformed (same rule as iOS). */
export function rankLadder(p: PuzzleDef): RankThresholds | null {
  const t = p.analytics?.rankThresholds;
  if (!Array.isArray(t) || t.length !== 5) return null;
  if (!t.every((n, i) => n > 0 && (i === 0 || n > t[i - 1]))) return null;
  return t;
}

let cachedDictionary: Promise<Dictionary> | null = null;

/**
 * `dict_v1` — the iOS `en_valid_words.txt` with the hard blocklist stripped and
 * lengths clamped to MIN_LEN..MAX_LEN, exactly as `longshot-gen` builds it. It
 * is a separate file from `full_words.txt` because hard-blocked words must be a
 * miss here, and puzzles are only valid against this exact build.
 */
export function loadLongshotDictionary(): Promise<Dictionary> {
  if (!cachedDictionary) {
    cachedDictionary = fetch(`${import.meta.env.BASE_URL}longshot_dict_v1.txt`)
      .then((r) => {
        if (!r.ok) throw new Error(`word list ${r.status}`);
        return r.text();
      })
      .then((text) => Dictionary.fromWordList(text, DICT_VERSION))
      .catch((e) => {
        cachedDictionary = null;
        throw e;
      });
  }
  return cachedDictionary;
}
